import { ApiError } from './errors';
import type { AuthTokens, AuthUser } from './types';

interface ApiEnvelope<T> {
  success: boolean;
  data: T;
  error?: { code: string; message: string };
}

interface RefreshResponse {
  accessToken: string;
  refreshToken: string;
  expiresAt: number;
  // The backend's refresh endpoint returns only the tokens, never the user
  // profile, so this is optional. On refresh the stored user must be preserved.
  user?: AuthUser;
}

export interface ApiClientConfig {
  baseUrl: string;
  getTokens(): AuthTokens | null | Promise<AuthTokens | null>;
  // `user` is optional: the token-refresh path has no fresh profile to pass, so
  // implementations must keep the previously stored user when it is undefined.
  setAuth(tokens: AuthTokens, user?: AuthUser): void | Promise<void>;
  clearAuth(): void | Promise<void>;
  /**
   * La sesion ha dejado de ser valida. `reason` trae el codigo del backend
   * (p. ej. AUTH_SESSION_INVALIDATED cuando otra sesion ha expulsado a esta),
   * para poder explicarle al usuario por que ha vuelto al login en lugar de
   * dejarlo ahi sin mas. Es opcional: quien no lo use no se entera.
   */
  onUnauthenticated(reason?: string): void;
  tokenRefreshThresholdMs?: number;
  /**
   * Corte de las peticiones. Sin el, en una red movil mala la promesa de fetch
   * puede no resolverse NUNCA y la pantalla se queda cargando para siempre:
   * los reintentos de react-query no ayudan porque nada falla.
   */
  requestTimeoutMs?: number;
  /** Las subidas de fichero necesitan mas margen que una peticion normal. */
  uploadTimeoutMs?: number;
}

export interface ApiRequestOptions extends RequestInit {
  /**
   * Treat this as a public/unauthenticated request: skip the silent
   * token-refresh and the 401 → onUnauthenticated() redirect. Use it for the
   * auth endpoints themselves (e.g. /auth/login), whose 401 means "bad
   * credentials" — NOT "session expired" — and must surface as a normal
   * ApiError so the form can show it instead of reloading the page.
   */
  skipAuth?: boolean;
}

export type ApiClient = <T>(endpoint: string, options?: ApiRequestOptions) => Promise<T>;

export function createApiClient(cfg: ApiClientConfig): ApiClient {
  const threshold = cfg.tokenRefreshThresholdMs ?? 60_000;
  const requestTimeout = cfg.requestTimeoutMs ?? 20_000;
  const uploadTimeout = cfg.uploadTimeoutMs ?? 60_000;
  let refreshPromise: Promise<void> | null = null;

  async function fetchWithTimeout(url: string, init: RequestInit, ms: number): Promise<Response> {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), ms);
    try {
      return await fetch(url, { ...init, signal: controller.signal });
    } catch (err) {
      // Solo el corte propio se traduce a NETWORK_TIMEOUT; un abort del
      // llamante o un fallo de red real se dejan pasar como son.
      if (controller.signal.aborted) {
        throw new ApiError(
          'NETWORK_TIMEOUT',
          'No hemos podido conectar. Comprueba tu conexion e intentalo de nuevo.',
          0,
        );
      }
      throw err;
    } finally {
      clearTimeout(timer);
    }
  }

  async function doRefresh(): Promise<void> {
    const tokens = await cfg.getTokens();
    if (!tokens) {
      await cfg.clearAuth();
      cfg.onUnauthenticated();
      return;
    }

    const res = await fetchWithTimeout(
      `${cfg.baseUrl}/auth/refresh`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refreshToken: tokens.refreshToken }),
      },
      requestTimeout,
    );

    if (!res.ok) {
      const errBody = (await res.json().catch(() => ({}))) as Partial<ApiEnvelope<never>>;
      await cfg.clearAuth();
      cfg.onUnauthenticated(errBody.error?.code);
      return;
    }

    const body = (await res.json()) as ApiEnvelope<RefreshResponse>;
    if (!body.success) {
      await cfg.clearAuth();
      cfg.onUnauthenticated(body.error?.code);
      return;
    }

    await cfg.setAuth(
      {
        accessToken: body.data.accessToken,
        refreshToken: body.data.refreshToken,
        expiresAt: body.data.expiresAt,
      },
      body.data.user,
    );
  }

  async function ensureFreshToken(): Promise<void> {
    const tokens = await cfg.getTokens();
    if (!tokens) return;
    if (Date.now() <= tokens.expiresAt - threshold) return;
    if (!refreshPromise) {
      refreshPromise = doRefresh().finally(() => {
        refreshPromise = null;
      });
    }
    await refreshPromise;
  }

  return async function apiClient<T>(
    endpoint: string,
    options: ApiRequestOptions = {},
  ): Promise<T> {
    const { skipAuth = false, ...fetchOptions } = options;

    if (!skipAuth) await ensureFreshToken();

    const tokens = await cfg.getTokens();

    // FormData / Blob bodies must let the browser set the correct multipart boundary
    // header; an explicit JSON Content-Type would break the request.
    const isMultipart =
      typeof FormData !== 'undefined' && fetchOptions.body instanceof FormData;

    const headers: Record<string, string> = {
      ...(isMultipart ? {} : { 'Content-Type': 'application/json' }),
      ...(fetchOptions.headers as Record<string, string>),
    };

    if (tokens?.accessToken) {
      headers['Authorization'] = `Bearer ${tokens.accessToken}`;
    }

    const timeout = isMultipart ? uploadTimeout : requestTimeout;
    const res = await fetchWithTimeout(`${cfg.baseUrl}${endpoint}`, { ...fetchOptions, headers }, timeout);

    if (res.status === 401 && !skipAuth) {
      if (!refreshPromise) {
        refreshPromise = doRefresh().finally(() => {
          refreshPromise = null;
        });
      }
      await refreshPromise;

      const newTokens = await cfg.getTokens();
      if (newTokens?.accessToken) {
        headers['Authorization'] = `Bearer ${newTokens.accessToken}`;
      }

      const retryRes = await fetchWithTimeout(
        `${cfg.baseUrl}${endpoint}`,
        { ...fetchOptions, headers },
        timeout,
      );

      if (!retryRes.ok) {
        const errBody = (await retryRes.json().catch(() => ({}))) as Partial<ApiEnvelope<never>>;
        throw new ApiError(
          errBody.error?.code ?? 'INTERNAL_ERROR',
          errBody.error?.message ?? 'Error desconocido',
          retryRes.status,
        );
      }

      if (retryRes.status === 204) return undefined as T;
      const retryBody = (await retryRes.json()) as ApiEnvelope<T>;
      return retryBody.data;
    }

    if (!res.ok) {
      const errBody = (await res.json().catch(() => ({}))) as Partial<ApiEnvelope<never>>;
      throw new ApiError(
        errBody.error?.code ?? 'INTERNAL_ERROR',
        errBody.error?.message ?? 'Error desconocido',
        res.status,
      );
    }

    if (res.status === 204) return undefined as T;
    const body = (await res.json()) as ApiEnvelope<T>;
    return body.data;
  };
}
